import firestore from '@react-native-firebase/firestore';
import {useAppSettings} from '../components/AppSettings';
import {
  Fund,
  LimitedPartner,
  minimumNumberSearchString,
  minimumTextSearchString,
  searchLimitedPartners,
  shouldSearch,
  useFunds,
  useSubscribers,
} from '../hooks/useDomainData';
import {
  GuestyGuest,
  GuestyReservation,
  useGuestyCustomFields,
  useGuestyReservations,
} from '../hooks/useGuestyData';
import {useLayoutInfo} from '../hooks/useLayoutInfo';
import {Linking, ScrollView, StyleSheet, View} from 'react-native';
import {LimitedPartnerComponent} from './LimitedPartners';
import {
  Button,
  Caption,
  DataTable,
  IconButton,
  Modal,
  Portal,
  Searchbar,
  Subheading,
  Text,
  Title,
  TouchableRipple,
} from 'react-native-paper';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import {memo, useCallback, useMemo, useState} from 'react';
import {useMutation, useQueryClient} from 'react-query';
import {AlertsProvider, useAlerts} from 'react-native-paper-alerts';
import {
  getCreditsUsedInfo,
  guestyCustomFieldIdToFundMap,
} from './GuestyReservationMatch';

// A map of GuestyGuest.id -> { Guest, Fund } tuples
type GuestyGuestToGuestFundMap = {
  [key: string]: {guest: GuestyGuest; fund: Fund; totalCredits: number}[];
};

const findMatchedGuests = (
  guestyReservations: GuestyReservation[],
  limitedPartners: LimitedPartner[],
  limitedPartnerFundMap: {[key: string]: Fund},
  fieldFundMap: guestyCustomFieldIdToFundMap,
): {
  matchedGuests: GuestyGuestToGuestFundMap;
  unmatchedGuests: GuestyGuestToGuestFundMap;
} => {
  // We'll sort into matched vs unmatched, but we need Guest:Fund tuples.
  // So we'll key off the guest, but store the array of those tuples that we need
  const newMatchedGuests: GuestyGuestToGuestFundMap = {};
  const newUnmatchedGuests: GuestyGuestToGuestFundMap = {};

  // const allFundCodes = Object.values(fieldFundMap).map(
  //   mapEntry => mapEntry.fund.fundCode,
  // );

  console.log('filtering for matches again');
  guestyReservations?.forEach(res => {
    // Get all the Funds needed for this reservation from credits/fund map on guestyRes
    res.creditsUsedMap = getCreditsUsedInfo(res, fieldFundMap);
    if (res.creditsUsedMap === undefined || res.creditsUsedMap.length < 1) {
      // console.log(`no credits for res ${res._id}`);
      return;
    }
    const resFunds: Fund[] =
      res.creditsUsedMap?.map<Fund>(creditsUsedMapEntry => {
        return creditsUsedMapEntry.fund;
      }) ?? [];

    const totalCreditsUsed =
      res.creditsUsedMap?.reduce(
        (sum, mapEntry) => sum + mapEntry.credits,
        0,
      ) ?? 0;

    // Use this to focus on a special case as needed
    // if (
    //   //   res.creditsUsedMap.length < 2
    //   //   // ||
    //   res.guest.fullName !== 'Archana Makam' &&
    //   false
    // ) {
    //   return;
    // }

    // console.log(
    //   `res ${res._id} (guest ${res.guest._id}) w/${res.creditsUsedMap?.length} (${resFunds.length} ?) funds / ${totalCreditsUsed} credits`,
    // );

    // For each fund with credits on this reservation...
    resFunds.forEach(resFund => {
      // Look up any previous matches
      const maybeMatched = newMatchedGuests[res.guest._id] ?? [];
      const maybeUnmatched = newUnmatchedGuests[res.guest._id] ?? [];

      const fundCreditsUsed =
        res.creditsUsedMap?.find(
          mapEntry => mapEntry.fund.fundCode === resFund.fundCode,
        )?.credits ?? 0;

      if (totalCreditsUsed > 0 && fundCreditsUsed === 0) {
        // this reservation did use credits, but just has a mandatory entry
        // for this fund, set to zero, we do not bother matching thos
        // console.log(
        //   `...total ${totalCreditsUsed} but ${fundCreditsUsed} for ${resFund.fundCode}, skip`,
        // );
        return;
      }

      if (maybeMatched !== undefined && fundCreditsUsed === 0) {
        // we have matched this guest and this is a zero-credit entry from a mandatory field, just skip it
        // console.log(
        //   `...zero credit entry for fund ${resFund.fundCode} on match ${res.guest.fullName}. Skip.`,
        // );
        return;
      }

      // Have we seen this guest:fund combo?
      const maybeMatch = maybeMatched.find(
        possibleMatch => possibleMatch.fund.fundCode === resFund.fundCode,
      );
      const maybeUnmatch = maybeUnmatched.find(
        possibleMatch => possibleMatch.fund.fundCode === resFund.fundCode,
      );
      // ...add these credits in if we have
      if (maybeMatch !== undefined) {
        // console.log(
        //   `  ...res:${resFund.fundCode} already sorted ${res._id} (${maybeMatch.totalCredits} prior credits)`,
        // );
        maybeMatch.totalCredits += fundCreditsUsed;
      }
      if (maybeUnmatch !== undefined) {
        // console.log(
        //   `  ...res:${resFund.fundCode} already sorted ${res._id} (${maybeUnmatch.totalCredits} prior credits)`,
        // );
        maybeUnmatch.totalCredits += fundCreditsUsed;
      }
      // if we have this investor in unmatched for any other fund, but with zero credits,
      // and we have them in matched now with non-zero credits, remove those unmatches
      if (
        maybeUnmatched !== undefined &&
        maybeMatch !== undefined &&
        maybeMatch.totalCredits > 0
      ) {
        for (let i = 0; i < maybeUnmatched.length; i++) {
          if (maybeUnmatched[i].totalCredits === 0) {
            // console.log(
            //   '...some credits now and found zero-unmatch entry. purgin.',
            // );
            maybeUnmatched.splice(i, 1);
          }
        }
        // if we removed the last array entry, nuke the whole map
        if (maybeUnmatched.length === 0) {
          delete newUnmatchedGuests[res.guest._id];
        }
      }

      if (maybeMatch !== undefined || maybeUnmatch !== undefined) {
        return;
      }

      // Filter LPs to just this fund...
      const fundLPs = limitedPartners.filter(
        lp => limitedPartnerFundMap[lp.ref.id].fundCode === resFund.fundCode,
      );

      // if one of these LPs has this guesty id linked - push matched
      const maybeMatchedFundLP = fundLPs.find(lp => {
        if (
          lp.guestyGuestIds?.find(
            lpGuestyId => lpGuestyId === res.guest._id,
          ) !== undefined
        ) {
          return lp;
        }
        return undefined;
      });
      if (maybeMatchedFundLP !== undefined) {
        if (newMatchedGuests[res.guest._id] === undefined) {
          newMatchedGuests[res.guest._id] = [];
        }
        newMatchedGuests[res.guest._id].push({
          guest: res.guest,
          fund: resFund,
          totalCredits: fundCreditsUsed,
        });
        // console.log(
        //   `...match for ${res.guest.fullName}:${resFund.fundCode}:${fundCreditsUsed}`,
        // );
        // if we have this investor in unmatched for any other fund, but with zero credits,
        // and we have them in matched now after a search, remove those unmatches
        if (maybeUnmatched !== undefined) {
          for (let i = 0; i < maybeUnmatched.length; i++) {
            // console.log(
            //   `...LP:guest lookup matched, unmatched zero entry? ${maybeUnmatched[i].fund.fundCode}:${maybeUnmatched[i].totalCredits}`,
            // );
            if (maybeUnmatched[i].totalCredits === 0) {
              // console.log(
              //   `...LP:guest lookup matched, and found zero-unmatch ${maybeUnmatched[i].fund.fundCode}. purgin.`,
              // );
              maybeUnmatched.splice(i, 1);
            }
          }
          // if we removed the last array entry, nuke the whole map
          if (maybeUnmatched.length === 0) {
            delete newUnmatchedGuests[res.guest._id];
          }
        }

        return;
      }

      // ...or last match resort, if an LP has this reservation linked, just skip
      if (
        fundLPs.find(
          lp =>
            lp.guestyReservationIds?.find(resId => res._id === resId) !==
            undefined,
        )
      ) {
        // console.log(`...LP direct match for ${resFund.fundCode}`);
        return;
      }

      // ...if no LP match for this fund on guest or reservation, push unmatched
      if (newUnmatchedGuests[res.guest._id] === undefined) {
        newUnmatchedGuests[res.guest._id] = [];
      }
      newUnmatchedGuests[res.guest._id].push({
        guest: res.guest,
        fund: resFund,
        totalCredits: fundCreditsUsed,
      });
      // console.log(`...no match for ${res.guest.fullName}:${resFund.fundCode}`);
      return;
    });
  });

  return {matchedGuests: newMatchedGuests, unmatchedGuests: newUnmatchedGuests};
};

function GuestyGuestMatch() {
  const appSettings = useAppSettings();
  const layoutInfo = useLayoutInfo();
  const queryClient = useQueryClient();
  const Alert = useAlerts();
  const guestyReservations = useGuestyReservations();
  const subscribers = useSubscribers();
  const funds = useFunds();
  const guestyCustomFields = useGuestyCustomFields();

  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [editGuest, setEditGuest] = useState<GuestyGuest | undefined>(
    undefined,
  );
  const [editGuestFund, setEditGuestFund] = useState<Fund | undefined>(
    undefined,
  );
  const [searchQuery, setSearchQuery] = useState('');
  const searchQueryLower = searchQuery.toLowerCase();
  const onChangeSearch = (query: string) => setSearchQuery(query);

  const fieldFundMap: guestyCustomFieldIdToFundMap = useMemo(() => {
    const map: guestyCustomFieldIdToFundMap = {};
    for (const fund of funds ?? []) {
      // - get funds with 'guestyCreditCustomFieldName' !== null
      if (fund.guestyCreditCustomFieldName) {
        // find the correct custom field name so we can get the custom field id
        const customField = (guestyCustomFields ?? []).find(
          field => field.displayName === fund.guestyCreditCustomFieldName,
        );
        // - create map of funds <-> custom field (with id + name)
        if (customField) {
          map[customField._id] = {fund, customField};
        }
      }
    }
    return map;
  }, [funds, guestyCustomFields]);

  const LinkToGuestyGuestButton = memo(function LinkToGuestyGuestButton({
    guestyId,
    color,
  }: {
    guestyId: string;
    color: string;
  }) {
    const openGuesty = useCallback(() => {
      Linking.openURL(
        `https://app.guesty.com/people/contact/${guestyId}/profile`,
      );
    }, [guestyId]);
    return (
      <TouchableRipple onPress={openGuesty}>
        <Caption style={{color}}>Guesty</Caption>
      </TouchableRipple>
    );
  });

  const {limitedPartners, limitedPartnerFundMap} = useMemo(() => {
    const filteredLimitedPartners: LimitedPartner[] = [];
    const lpFundMap: {[key: string]: Fund} = {};
    if (subscribers !== undefined) {
      // console.log(
      //   'Subscribers pre-filter for acceptingReservations ' +
      //     subscribers.length,
      // );
      const filteredSubs = subscribers.filter(sub => {
        return sub.fund?.acceptingReservations === true;
      });
      // console.log('Subscriber count filtered ' + filteredSubs.length);

      filteredSubs.forEach(sub => {
        sub.limitedPartners?.forEach(lp => {
          filteredLimitedPartners.push(lp);
          lpFundMap[lp.ref.id] = sub.fund!;
        });
      });
    }
    return {
      limitedPartners: filteredLimitedPartners,
      limitedPartnerFundMap: lpFundMap,
    };
  }, [subscribers]);

  const {matchedGuests, unmatchedGuests} = findMatchedGuests(
    guestyReservations ?? [],
    limitedPartners,
    limitedPartnerFundMap,
    fieldFundMap,
  );

  // console.log(`matchedGuests: ${JSON.stringify(matchedGuests, null, 2)}`);
  // console.log(`unMatchedGuests: ${JSON.stringify(unmatchedGuests, null, 2)}`);

  // console.log(`matchedGuests keys? ${Object.keys(matchedGuests).length}`);
  // console.log(`matchedGuests values? ${Object.values(matchedGuests).length}`);

  const saveLimitedPartnerGuestIds = useMutation(
    (limitedPartner: LimitedPartner) => {
      console.log('in saveLimitedPartnerGuestIds');

      console.log(
        `going to save: ${JSON.stringify(
          limitedPartner.guestyGuestIds,
        )} into doc ${limitedPartner.ref.id}`,
      );

      return firestore()
        .collection('limitedPartners')
        .doc(limitedPartner.ref.id)
        .update({
          guestyGuestIds: limitedPartner.guestyGuestIds,
        });
    },
    {
      onSettled: () => {
        console.log('save succeeded');
        return queryClient.invalidateQueries([
          'limitedPartners',
          'subscribers',
        ]);
      },
      onError: e => {
        Alert.alert('Error', 'There was an error while saving: ' + e, [
          {text: appSettings.t('OK')},
        ]);
      },
    },
  );

  const searchedLimitedPartners = useMemo(
    () =>
      searchLimitedPartners(
        searchQueryLower,
        editGuestFund,
        limitedPartners,
        limitedPartnerFundMap,
      ),
    [searchQueryLower, editGuestFund, limitedPartners, limitedPartnerFundMap],
  );

  const themedStyles = StyleSheet.create({
    modalContent: {
      margin: 60,
      padding: 20,
      borderColor: appSettings.currentTheme.colors.border,
      borderWidth: 2,
      backgroundColor: appSettings.currentTheme.colors.background,
      position: 'absolute',
      maxWidth: layoutInfo.screen.width - 120,
      maxHeight: layoutInfo.screen.height - 160,
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
    },
  });

  const loading =
    limitedPartners === undefined || !guestyReservations === undefined;
  console.log('we are rendering again');

  return (
    <>
      <View style={styles.horizontalCentered}>
        <Title style={styles.title}>Guesty Guest/LP Matcher</Title>
      </View>
      <ScrollView
        persistentScrollbar={true}
        style={[styles.container, styles.content]}>
        <View style={styles.horizontalCentered}>
          <Subheading style={styles.title}>Unmatched Guests</Subheading>
        </View>
        <DataTable>
          {loading && (
            <DataTable.Row>
              <DataTable.Header>
                <DataTable.Title>loading...</DataTable.Title>
              </DataTable.Header>
            </DataTable.Row>
          )}
          {!loading && (
            <DataTable.Header style={styles.shortHeader}>
              <DataTable.Title style={styles.widerCell}>
                Guesty Guest
              </DataTable.Title>
              <DataTable.Title style={styles.widerCell}>Fund</DataTable.Title>
              <DataTable.Title style={styles.widerCell}>
                Limited Partners
              </DataTable.Title>
            </DataTable.Header>
          )}
          {!loading && Object.keys(unmatchedGuests).length === 0 && (
            <DataTable.Row>
              <DataTable.Cell>No unmatched Guesty Guests found.</DataTable.Cell>
            </DataTable.Row>
          )}
          {!loading &&
            Object.keys(unmatchedGuests).length > 0 &&
            // For all the tuple arrays in our guest-id -> (guest:fund)-array map...
            Object.values(unmatchedGuests)
              // ...sort them by last name
              .sort((a, b) =>
                a[0].guest.lastName.localeCompare(b[0].guest.lastName),
              )
              // ...then for array roll through each entry and spit out the UI for matching
              .map(guestFundMapArray =>
                guestFundMapArray.map(guestyFundMapEntry => (
                  <DataTable.Row
                    key={
                      guestyFundMapEntry.guest._id +
                      guestyFundMapEntry.fund.fundCode
                    }>
                    <DataTable.Cell style={styles.widerCell}>
                      {guestyFundMapEntry.guest.fullName} (
                      <LinkToGuestyGuestButton
                        guestyId={guestyFundMapEntry.guest._id}
                        color={appSettings.currentTheme.colors.primary}
                      />
                      )
                    </DataTable.Cell>
                    <DataTable.Cell style={styles.widerCell}>
                      {`${guestyFundMapEntry.fund.fundCode} (for ${guestyFundMapEntry.totalCredits} credits)`}
                    </DataTable.Cell>
                    <DataTable.Cell style={styles.widerCell}>
                      Find Matching Limited Partner
                      <IconButton
                        icon="pencil"
                        size={18}
                        color={appSettings.currentTheme.colors.primary}
                        onPress={() => {
                          console.log(
                            `guest ${JSON.stringify(
                              guestyFundMapEntry.guest,
                              null,
                              2,
                            )}`,
                          );
                          setEditGuestFund(
                            guestyFundMapEntry.totalCredits > 0
                              ? guestyFundMapEntry.fund
                              : undefined,
                          );
                          setSearchQuery(guestyFundMapEntry.guest.lastName);
                          setEditGuest(guestyFundMapEntry.guest);
                          setEditDialogVisible(true);
                        }}
                      />
                    </DataTable.Cell>
                  </DataTable.Row>
                )),
              )}
        </DataTable>
        <View style={styles.horizontalCentered}>
          <Subheading style={styles.title}>Matched Guests</Subheading>
        </View>
        <DataTable>
          {loading && (
            <DataTable.Row>
              <DataTable.Header>
                <DataTable.Title>loading...</DataTable.Title>
              </DataTable.Header>
            </DataTable.Row>
          )}
          {!loading && (
            <DataTable.Header style={styles.shortHeader}>
              <DataTable.Title style={styles.widerCell}>
                Guest Name
              </DataTable.Title>
              <DataTable.Title style={styles.widerCell}>Fund</DataTable.Title>
              <DataTable.Title style={styles.widerCell}>
                Limited Partner
              </DataTable.Title>
              <DataTable.Title style={styles.smallCell}>
                Unmatch
              </DataTable.Title>
            </DataTable.Header>
          )}
          {!loading && Object.keys(matchedGuests).length === 0 && (
            <DataTable.Row>
              <DataTable.Cell>No Matched Guests found.</DataTable.Cell>
            </DataTable.Row>
          )}
          {!loading &&
            Object.keys(matchedGuests).length > 0 &&
            // For all the tuple arrays in our guest-id -> (guest:fund)-array map...
            Object.values(matchedGuests)
              // ...sort them by last name
              .sort((a, b) =>
                a[0].guest.lastName.localeCompare(b[0].guest.lastName),
              )
              // ...then for array roll through each entry and spit out the UI for matching
              .map(guestFundMapArray =>
                guestFundMapArray.map(guestyFundMapEntry => (
                  <DataTable.Row
                    key={
                      guestyFundMapEntry.guest._id +
                      guestyFundMapEntry.fund.fundCode
                    }>
                    <DataTable.Cell style={styles.widerCell}>
                      {guestyFundMapEntry.guest.fullName} (
                      <LinkToGuestyGuestButton
                        guestyId={guestyFundMapEntry.guest._id}
                        color={appSettings.currentTheme.colors.primary}
                      />
                      )
                    </DataTable.Cell>
                    <DataTable.Cell style={styles.widerCell}>
                      {guestyFundMapEntry.fund.fundCode}
                    </DataTable.Cell>
                    <DataTable.Cell style={styles.widerCell}>
                      {
                        limitedPartners.find(lp =>
                          lp.guestyGuestIds?.includes(
                            guestyFundMapEntry.guest._id,
                          ),
                        )?.limitedPartnerName
                      }
                    </DataTable.Cell>
                    <DataTable.Cell style={styles.smallCell}>
                      <TouchableRipple
                        onPress={() => {
                          const limitedPartner = limitedPartners.find(lp =>
                            lp.guestyGuestIds?.includes(
                              guestyFundMapEntry.guest._id,
                            ),
                          );
                          if (!limitedPartner) {
                            console.warn(
                              `LP for guest ${guestyFundMapEntry.guest.fullName} unfound?`,
                            );
                            return;
                          }

                          console.log(
                            `removing ${guestyFundMapEntry.guest.fullName} from ${limitedPartner.limitedPartnerName}`,
                          );
                          limitedPartner.guestyGuestIds =
                            limitedPartner.guestyGuestIds?.filter(
                              guestId =>
                                guestId !== guestyFundMapEntry.guest._id,
                            );
                          saveLimitedPartnerGuestIds.mutate(limitedPartner);
                        }}>
                        <View style={styles.row}>
                          <Icon
                            style={styles.horizontalPad}
                            color={appSettings.currentTheme.colors.primary}
                            name="link-off"
                            size={20}
                          />
                        </View>
                      </TouchableRipple>
                    </DataTable.Cell>
                  </DataTable.Row>
                )),
              )}
        </DataTable>
      </ScrollView>
      <Portal>
        <Modal
          contentContainerStyle={themedStyles.modalContent}
          visible={editDialogVisible}
          onDismiss={() => setEditDialogVisible(false)}>
          <AlertsProvider>
            <View style={[styles.topContent, styles.center]}>
              <Title>
                Finding Limited Partner for Guest: {editGuest?.fullName}
                {editGuestFund !== undefined
                  ? ' in fund ' + editGuestFund.fundCode
                  : ''}
              </Title>
              <Text>
                Guest Phone?: {editGuest?.phone}, Guest Email?{' '}
                {editGuest?.email}
              </Text>
              <Searchbar
                placeholder="Search"
                onChangeText={onChangeSearch}
                value={searchQuery}
              />
            </View>
            <View style={styles.bottomContent}>
              <ScrollView
                persistentScrollbar={true}
                contentContainerStyle={styles.modalScrollViewStyle}>
                <DataTable>
                  <DataTable.Header>
                    <DataTable.Title>Limited Partner</DataTable.Title>
                    <DataTable.Title style={styles.smallCell}>
                      Save
                    </DataTable.Title>
                  </DataTable.Header>
                  {!shouldSearch(searchQuery) && (
                    <DataTable.Row>
                      <DataTable.Cell>
                        No search in effect. Type {minimumTextSearchString} or
                        more characters (or {minimumNumberSearchString} or more
                        numbers) above.
                      </DataTable.Cell>
                    </DataTable.Row>
                  )}
                  {shouldSearch(searchQuery) &&
                    searchedLimitedPartners?.length === 0 && (
                      <DataTable.Row>
                        <DataTable.Cell style={styles.center}>
                          No search results found
                        </DataTable.Cell>
                      </DataTable.Row>
                    )}
                  {(searchedLimitedPartners?.length ?? 0) > 0 &&
                    searchedLimitedPartners?.map(lp => (
                      <DataTable.Row key={lp.limitedPartnerId}>
                        <DataTable.Cell>
                          <LimitedPartnerComponent
                            limitedPartner={lp}
                            fund={limitedPartnerFundMap[lp.ref.id]}
                            // readOnly={true}
                          />
                        </DataTable.Cell>
                        <DataTable.Cell style={styles.smallCell}>
                          <Button
                            key="Save"
                            onPress={() => {
                              console.log(`match lp ${lp.limitedPartnerName}`);
                              const newLP = {
                                guestyGuestIds: lp.guestyGuestIds,
                                ref: lp.ref,
                              };
                              if (!newLP.guestyGuestIds) {
                                newLP.guestyGuestIds = [];
                              }
                              if (
                                editGuest !== undefined &&
                                !newLP.guestyGuestIds.includes(editGuest._id)
                              ) {
                                newLP.guestyGuestIds.push(editGuest._id);
                                saveLimitedPartnerGuestIds.mutate(
                                  newLP as LimitedPartner,
                                );
                                setSearchQuery('');
                              }
                              setEditDialogVisible(false);
                            }}>
                            Select
                          </Button>
                        </DataTable.Cell>
                      </DataTable.Row>
                    ))}
                </DataTable>
              </ScrollView>

              <View style={styles.horizontalRight}>
                <Button
                  key="Cancel"
                  onPress={() => setEditDialogVisible(false)}>
                  Cancel
                </Button>
              </View>
            </View>
          </AlertsProvider>
        </Modal>
      </Portal>
    </>
  );
}

const styles = StyleSheet.create({
  shortHeader: {height: 25},
  horizontalPad: {paddingHorizontal: 10},
  row: {flexDirection: 'row'},
  content: {flex: 1, paddingHorizontal: 20},
  topContent: {
    flex: 0.4,
    paddingHorizontal: 20,
  },
  bottomContent: {
    flex: 1,
    paddingHorizontal: 20,
  },
  center: {alignItems: 'center'},
  touchableRow: {
    paddingHorizontal: 5,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  widerCell: {
    minWidth: 150,
    width: 100,
    justifyContent: 'center',
    alignItems: 'center',
  },
  smallCell: {
    maxWidth: 80,
    width: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  borderCell: {
    borderStartWidth: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  container: {
    flex: 1,
    position: 'relative',
  },
  title: {
    padding: 10,
    paddingHorizontal: 20,
  },
  horizontalCentered: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    alignContent: 'center',
    paddingHorizontal: 10,
  },
  horizontalRight: {
    flexDirection: 'row',
    alignContent: 'flex-end',
    justifyContent: 'flex-end',
  },
  modalScrollViewStyle: {
    // borderWidth: 1,
    // borderColor: 'green',
    flex: 1,
    flexDirection: 'column',
  },
});

export default GuestyGuestMatch;
